import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { get, post } from "api";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { loadIfFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";

const loader = async (weightLossSubscriptionId: string | undefined) => {
  const weightLossPrograms = queryClient.fetchQuery(
    ["/healthie/subscriptions"],
    () => get("/healthie/subscriptions")
  );
  const weightLossProgram = (await weightLossPrograms).find(
    (wlp) => wlp.id === weightLossSubscriptionId
  );
  let transactionDetails;
  if (weightLossProgram) {
    transactionDetails = await post(
      "/healthie/subscription/{healthieSubscriptionId}/transactions",
      { healthieSubscriptionId: weightLossProgram.id }
    );
  } else {
    transactionDetails = null;
  }
  return waitObj({ transactionDetails, weightLossProgram });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;

export const weightLossProgramCheckoutLoader: LoaderFunction = loadIfFlag(
  "repeatmd-glp"
)(({ params }) => {
  return loader(params.id);
});

const createHealthieUser = async (
  shippingAddress: StripeAddressElementChangeEvent["value"] | null,
  billingAddress: StripeAddressElementChangeEvent["value"] | null
) => {
  const questionnaire = JSON.parse(
    localStorage.getItem("glpQuestionnaire") || "{}"
  );

  const height =
    (
      parseInt(questionnaire.Height) * 12 +
      parseInt(questionnaire.Inches)
    ).toString() || "";

  // if the user provided a shipping address, use it for the questionnaire
  // otherwise, use the billing address
  questionnaire.Address =
    shippingAddress?.address?.line1 || billingAddress?.address?.line1;
  questionnaire.City =
    shippingAddress?.address?.city || billingAddress?.address?.city;
  questionnaire.State =
    shippingAddress?.address?.state || billingAddress?.address?.state;
  questionnaire.Zip =
    shippingAddress?.address?.postal_code ||
    billingAddress?.address?.postal_code;
  questionnaire.Country =
    shippingAddress?.address?.country || billingAddress?.address?.country;
  questionnaire.Height = height;

  // Image Upload will be taken out of the questionnaire for the request
  const imageUpload = questionnaire.ImageUpload as string;
  delete questionnaire.ImageUpload;

  const healthieUser = await post("/healthie/user", {
    questionnaireAnswers: questionnaire,
    imageUpload,
  });
  localStorage.setItem("patientModality", healthieUser.modality);
  return healthieUser;
};

const CheckoutForm = ({
  weightLossProgramId,
}: {
  weightLossProgramId: string;
}) => {
  const navigate = useNavigate();
  const elements = useElements();
  const stripe = useStripe();
  const stripeRedirect = `/shop/weight-loss-program/checkout/${weightLossProgramId}/success`;
  const [billingAddress, setBillingAddress] = useState<
    StripeAddressElementChangeEvent["value"] | null
  >(null);
  const [shippingAddress, setShippingAddress] = useState<
    StripeAddressElementChangeEvent["value"] | null
  >(null);

  const { mutate: handleSubmit } = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) {
        return null;
      }
      const { paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: new URL(stripeRedirect, location.href).href,
        },
        redirect: "if_required",
      });

      // Optimistically handle post-payment actions
      try {
        if (paymentIntent?.status === "succeeded") {
          await createHealthieUser(shippingAddress, billingAddress);
          navigate(stripeRedirect);
        }
      } catch (e) {
        console.info("Error creating healthie user");
        // Navigate to the success page regardless of the error
        navigate(stripeRedirect);
      }
    },
  });

  if (!stripe || !elements) {
    return null;
  }

  return (
    <>
      <PaymentElement />
      <p className="pt-4  text-bold1 text-primary">Billing Address</p>
      <AddressElement
        options={{ mode: "billing" }}
        onChange={(e) => {
          setBillingAddress(e.value);
        }}
      />
      <p className="pt-4  text-bold1 text-primary">Shipping Address</p>
      <AddressElement
        options={{ mode: "shipping" }}
        onChange={(e) => {
          setShippingAddress(e.value);
        }}
      />
      <div className="pt-6">
        <Button
          onClick={() => {
            handleSubmit();
          }}
          fullWidth
        >
          Place Order
        </Button>
      </div>
    </>
  );
};

const WeightLossProgramCheckout = () => {
  const stripeKey = useStripePublishableKey();

  const stripePromise = stripeLoader.getStripe(stripeKey);

  const { transactionDetails: data, weightLossProgram } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();
  if (!data || !weightLossProgram) {
    return null;
  }

  return (
    <>
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
        showHeaderItems={false}
      >
        <div className="flex max-h-[32px] items-center">
          <div className="flex w-full items-center justify-center text-sub2 text-primary">
            CHECKOUT
          </div>
        </div>
      </TopNav>
      <div className="flex flex-col">
        <div className="px-5 pb-5 pt-4">
          <div className="flex flex-col items-center">
            <div className="text-center font-serif text-h1 text-primary">
              {weightLossProgram.name}
            </div>
            <div className="flex pt-3">
              <span className="text-sub1 text-primary">
                {formatCurrency(weightLossProgram.monthlyCost)}/
              </span>
              <span className="text-body1 text-primary">month</span>
            </div>
          </div>
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: data.clientSecret }}
            key={data.paymentIntentId}
          >
            <CheckoutForm weightLossProgramId={weightLossProgram.id} />
          </Elements>
        </div>
      </div>
    </>
  );
};

export default WeightLossProgramCheckout;
